import { PartialDict, Sample, type SamplingPoint } from '@resistapp/common/types';
import { get, isNil, uniq } from 'lodash';
import { z } from 'zod';

/*
    SamplingPointType represents the high-level category of the sampled location, indicating the origin process of the sample.

    NOTE WHEN UPDATING SamplingPointTypes, THEIR NAMES OR SUB-TYPE MAPPINGS!
    - Migrate existing values in database if renaming types
    - Update SamplingPointType, getParentType, friendlySamplingPointSubType and friendlySamplingPointType
    - Update Customer template 'Sample types™ sheet and named ranges
    - Add backward compatibility logic for query params (getSamplingPointTypeParam)
    - Update sample-form-validaiton.gs (and its active version in sheets, might not be used?)
    - Update comparableGroupSpecs if needed
*/
export enum SampleType {
  WASTEWATER = 'WASTEWATER',
  SURFACE_WATER = 'SURFACE_WATER',
  OTHER_WATER = 'OTHER_WATER',
  SEDIMENT = 'SEDIMENT',
  SLUDGE = 'SLUDGE',
  SOIL = 'SOIL',
  STOOL = 'STOOL',
  MANURE = 'MANURE',
  FOOD = 'FOOD',
  CONTROL = 'CONTROL',
  OTHER = 'OTHER',
}

export const zSampleType = z.nativeEnum(SampleType);

export const sampleTypeByName: PartialDict<SampleType> = Object.fromEntries(
  Object.entries(SampleType).map(([key, value]) => [friendlySampleType(value), key as SampleType]),
);
export const SamplingPointTypeWithAllAndMatching = {
  ...SampleType,
  ALL: 'ALL',
} as const;

export enum SmplPntSubTypePostFix {
  _RAW = '_RAW',
  _TREATED = '_TREATED',
  _UPSTREAM = '_UPSTREAM',
  _DOWNSTREAM = '_DOWNSTREAM',
}

export const beforeSubtypePostfixes = [SmplPntSubTypePostFix._RAW, SmplPntSubTypePostFix._UPSTREAM];
export const afterSubtypePostfixes = [SmplPntSubTypePostFix._TREATED, SmplPntSubTypePostFix._DOWNSTREAM];

export type SampleWithSamplingPoint = {
  id?: number;
  lat?: number;
  lon?: number;
  samplingPoint: Omit<SamplingPoint, 'id'> & { id?: number };
  time?: Sample['time'] | string;
};
export function getSamplingPointSubtypePoolingKey<T extends SampleWithSamplingPoint>(sample: T): string {
  const { samplingPoint } = sample;
  const { lat, lon } = samplingPoint;
  const key = getSampleSubTypeBaseString(samplingPoint.subtype);
  const poolById = isNil(lat) || isNil(lon) || isNil(samplingPoint.subtype) || key === samplingPoint.subtype;
  if (poolById && !samplingPoint.id) {
    throw new Error(`Sample ${sample.id} missing sampling point id in getSamplingPointSubtypePoolingKey`);
  }
  return poolById ? `${samplingPoint.id}` : `${key}-${lat}-${lon}`;
}

export function getSampleSubTypeBaseString(samplingPointSubType: SampleSubType | null | undefined): string | undefined {
  const postfixPattern = Object.values(SmplPntSubTypePostFix).join('|');
  const regex = new RegExp(`(${postfixPattern})$`, 'g');
  return samplingPointSubType?.replace(regex, '');
}

export function getSiteCountFromSamples(samples: Sample[]) {
  return uniq(
    Object.values(samples).map(sample => getSamplingPointSubtypePoolingKey(sample as SampleWithSamplingPoint)),
  ).length;
}

export function isBeforeSample(sample: { samplingPoint: { subtype: SampleSubType | null | undefined } }): boolean {
  return isBeforeSubtype(sample.samplingPoint.subtype);
}

export function isAfterSample(sample: { samplingPoint: { subtype: SampleSubType | null | undefined } }): boolean {
  return isAfterSubtype(sample.samplingPoint.subtype);
}

export function isBeforeSubtype(subtype: SampleSubType | undefined | null): boolean {
  return !!subtype?.endsWith(SmplPntSubTypePostFix._RAW) || !!subtype?.endsWith(SmplPntSubTypePostFix._UPSTREAM);
}

export function isAfterSubtype(subtype: SampleSubType | undefined | null): boolean {
  return !!subtype?.endsWith(SmplPntSubTypePostFix._TREATED) || !!subtype?.endsWith(SmplPntSubTypePostFix._DOWNSTREAM);
}

export function isWaterType(type: SampleType): boolean {
  return type === SampleType.WASTEWATER || type === SampleType.SURFACE_WATER || type === SampleType.OTHER_WATER;
}

/*
    SamplingPointSubType represents lower-level categories of the sampled location, indicating the ORIGIN PROCESS of the sample.
    SamplingPointSubType is a child of SamplingPointType in a is-a hierarchy, and subtypes should represent MECE subsets of the parent types.

    NOTE WHEN UPDATING SamplingPointSubTypes, THEIR NAMES OR TYPE MAPPINGS!
    - _RAW, _TREATED, _UPSTREAM, _DOWNSTREAM suffixes are expected for pooling samples for process performance evaluation (see: SmplPntSubTypePostFix)
    - Update comparableGroupSpecs if needed
    - Update SamplingPointSubType, getParentType and friendlySamplingPointSubType
    - Update LAB FM 002 Customer template 'Sample types™ sheet (default and ARG 2.1 versions)
      - https://docs.google.com/spreadsheets/d/1Kd-SBfhDULKflySD0tRbu4kUYF-zf25OFvMmgdM9sXY/edit?gid=707127087#gid=707127087
      - https://docs.google.com/spreadsheets/d/11pfBa4iMZfSDHKavy4ZnkdPowqoE3BlSOZmi0prUo0Y/edit?gid=1162661353#gid=1162661353

    TODO: add links between sampling points (instead of _raw/_treated, _upstream/_downstream)
    TODO: add a separate field for intended use (next steps in the process)
*/
export enum SampleSubType {
  // WASTEWATER
  HOSPITAL_WASTEWATER_RAW = 'HOSPITAL_WASTEWATER_RAW', // clinical
  HOSPITAL_WASTEWATER_TREATED = 'HOSPITAL_WASTEWATER_TREATED',
  PRIMARY_HEALTH_CARE_RAW = 'PRIMARY_HEALTH_CARE_RAW', // clinical, Clinic / terverysasema like mehiläinen or kalasatama
  PRIMARY_HEALTH_CARE_TREATED = 'PRIMARY_HEALTH_CARE_TREATED',
  URBAN_WASTEWATER_RAW = 'URBAN_WASTEWATER_RAW', // population
  URBAN_WASTEWATER_TREATED = 'URBAN_WASTEWATER_TREATED',
  INDUSTRIAL_WASTEWATER_RAW = 'INDUSTRIAL_WASTEWATER_RAW',
  INDUSTRIAL_WASTEWATER_TREATED = 'INDUSTRIAL_WASTEWATER_TREATED',
  AGRICULTURAL_WASTEWATER_RAW = 'AGRICULTURAL_WASTEWATER_RAW',
  AGRICULTURAL_WASTEWATER_TREATED = 'AGRICULTURAL_WASTEWATER_TREATED',
  AQUACULTURE_WASTEWATER_RAW = 'AQUACULTURE_WASTEWATER_RAW',
  AQUACULTURE_WASTEWATER_TREATED = 'AQUACULTURE_WASTEWATER_TREATED',
  SEPTIC_TANK_RAW = 'SEPTIC_TANK_RAW', // population
  SEPTIC_TANK_TREATED = 'SEPTIC_TANK_TREATED',
  GRAY_WATER = 'GRAY_WATER',
  BLACK_WATER = 'BLACK_WATER', // population
  STORMWATER = 'STORMWATER', // Monsuun / melting snow
  URBAN_RUNOFF = 'URBAN_RUNOFF', // population, mix off, stormwater, irrigation water runoff, washwater, oil, fuel chemical spills and leaks, sanitary sewer overflows, ground water infiltration, construction runoff, residential and commercial runoff
  UNSPECIFIED_SEWAGE_WASTEWATER = 'UNSPECIFIED_SEWAGE_WASTEWATER',
  OTHER_WASTEWATER = 'OTHER_WASTEWATER',

  // SURFACE WATER
  SEAWATER = 'SEAWATER',
  UNSPECIFIED_SURFACE_WATER = 'UNSPECIFIED_SURFACE_WATER', // Only for old samples, not on sheet
  UNSPECIFIED_SURFACE_WATER_UPSTREAM = 'UNSPECIFIED_SURFACE_WATER_UPSTREAM', // Only for old samples, not on sheet
  UNSPECIFIED_SURFACE_WATER_DOWNSTREAM = 'UNSPECIFIED_SURFACE_WATER_DOWNSTREAM', // Only for old samples, not on sheet
  ESTUARY_WATER_UPSTREAM = 'ESTUARY_WATER_UPSTREAM',
  ESTUARY_WATER_DOWNSTREAM = 'ESTUARY_WATER_DOWNSTREAM',
  COASTAL_WATER_UPSTREAM = 'COASTAL_WATER_UPSTREAM',
  COASTAL_WATER_DOWNSTREAM = 'COASTAL_WATER_DOWNSTREAM',
  FRESH_RIVER_WATER_UPSTREAM = 'FRESH_RIVER_WATER_UPSTREAM', // Streaming fresh water w/o or before dumping anything into it (within the project consideration)
  FRESH_RIVER_WATER_DOWNSTREAM = 'FRESH_RIVER_WATER_DOWNSTREAM',
  FRESH_LAKE_WATER = 'FRESH_LAKE_WATER',
  WETLAND_WATER = 'WETLAND_WATER',

  // OTHER_WATER
  SPRING_WATER = 'SPRING_WATER',
  RAINWATER = 'RAINWATER', // without touching the ground
  GROUNDWATER = 'GROUNDWATER',
  INDUSTRIAL_WATER = 'INDUSTRIAL_WATER',
  DRINKING_WATER = 'DRINKING_WATER',
  RECLAIMED_WATER = 'RECLAIMED_WATER',
  SHRIMP_CULTURE_WATER = 'SHRIMP_CULTURE_WATER',
  FISH_CULTURE_WATER = 'FISH_CULTURE_WATER',
  CHEMICALLY_DISINFECTED_WATER = 'CHEMICALLY_DISINFECTED_WATER',

  // SEDIMENT
  SEDIMENT_RIVER = 'SEDIMENT_RIVER',
  SEDIMENT_LAKE = 'SEDIMENT_LAKE',
  SEDIMENT_BRACKISH = 'SEDIMENT_BRACKISH',
  SEDIMENT_SEABED = 'SEDIMENT_SEABED',
  SHRIMP_CULTURE_SEDIMENT = 'SHRIMP_CULTURE_SEDIMENT',
  FISH_CULTURE_SEDIMENT = 'FISH_CULTURE_SEDIMENT',

  // SOIL
  AGRICULTURAL_SOIL = 'AGRICULTURAL_SOIL',
  URBAN_SOIL = 'URBAN_SOIL',
  FOREST_SOIL = 'FOREST_SOIL',
  OTHER_NATURAL_SOIL = 'OTHER_NATURAL_SOIL',
  SAND = 'SAND',
  OTHER_SOIL = 'OTHER_SOIL',

  // STOOL - fresh excrement collected directly from the animals
  HUMAN_STOOL = 'HUMAN_STOOL',
  WILD_ANIMAL_STOOL = 'WILD_ANIMAL_STOOL',
  CAT_STOOL = 'CAT_STOOL',
  DOG_STOOL = 'DOG_STOOL',
  OTHER_COMPANION_ANIMAL_STOOL = 'OTHER_COMPANION_ANIMAL_STOOL',
  PRODUCTION_CATTLE_STOOL = 'PRODUCTION_CATTLE_STOOL',
  PRODUCTION_SWINE_STOOL = 'PRODUCTION_SWINE_STOOL',
  PRODUCTION_POULTRY_STOOL = 'PRODUCTION_POULTRY_STOOL',
  PRODUCTION_SHEEP_STOOL = 'PRODUCTION_SHEEP_STOOL',
  PRODUCTION_ANIMAL_OTHER_STOOL = 'PRODUCTION_ANIMAL_OTHER_STOOL',

  // MANURE - collected, stored, or treated excrement possibly mixed with urine or ground
  CATTLE_MANURE = 'CATTLE_MANURE',
  SWINE_MANURE = 'SWINE_MANURE',
  SHEEP_MANURE = 'SHEEP_MANURE',
  POULTRY_MANURE = 'POULTRY_MANURE',
  OTHER_MANURE = 'OTHER_MANURE',

  // SLUDGE
  ACTIVATED_SLUDGE = 'ACTIVATED_SLUDGE',
  SEWAGE_SLUDGE = 'SEWAGE_SLUDGE',

  // FOOD
  BEEF = 'BEEF',
  PORK = 'PORK',
  POULTRY_MEAT = 'POULTRY_MEAT',
  FISH = 'FISH',
  SHELLFISH = 'SHELLFISH',
  SHRIMP = 'SHRIMP',
  MEAT_OTHER = 'MEAT_OTHER',
  DAIRY = 'DAIRY',
  FRUITS = 'FRUITS',
  VEGETABLES = 'VEGETABLES',
  LEGUMES = 'LEGUMES',
  OTHER_PLANT_BASED_FOOD = 'PLANT_BASED_OTHER',
  FOOD_OTHER = 'FOOD_OTHER',

  // CONTROL
  NTC = 'NTC',
  POSITIVE_CONTROL = 'POSITIVE_CONTROL',
  OTHER_CONTROL = 'OTHER_CONTROL',

  // OTHER
  ISOLATE = 'ISOLATE',
  BIOFILM = 'BIOFILM',
  ENVIRONMENTAL_SWAB = 'ENVIRONMENTAL_SWAB',
  ANIMAL_FEED = 'ANIMAL_FEED',
  COMPOST = 'COMPOST',
  DUST = 'DUST',
  AIR = 'AIR',
  BLOOD_HUMAN = 'BLOOD_HUMAN',
}

export const zSampleSubType = z.nativeEnum(SampleSubType);
export const sampleSubtypeByName: PartialDict<SampleType> = Object.fromEntries(
  Object.entries(SampleSubType).map(([key, value]) => [
    friendlySampleSubType(value, getParentType(value)),
    key as SampleType,
  ]),
);

export function assertSmplPntTypes(subType: SampleSubType | null | undefined, type: SampleType) {
  if (!isLegalSubtype(subType, type)) {
    throw new Error(`Invalid sampling point subtype ${subType} for type ${type}`);
  }
}

export function getParentType(subType: SampleSubType): SampleType {
  switch (subType) {
    case SampleSubType.HOSPITAL_WASTEWATER_RAW:
    case SampleSubType.HOSPITAL_WASTEWATER_TREATED:
    case SampleSubType.PRIMARY_HEALTH_CARE_RAW:
    case SampleSubType.PRIMARY_HEALTH_CARE_TREATED:
    case SampleSubType.URBAN_WASTEWATER_RAW:
    case SampleSubType.URBAN_WASTEWATER_TREATED:
    case SampleSubType.INDUSTRIAL_WASTEWATER_RAW:
    case SampleSubType.INDUSTRIAL_WASTEWATER_TREATED:
    case SampleSubType.AGRICULTURAL_WASTEWATER_RAW:
    case SampleSubType.AGRICULTURAL_WASTEWATER_TREATED:
    case SampleSubType.AQUACULTURE_WASTEWATER_RAW:
    case SampleSubType.AQUACULTURE_WASTEWATER_TREATED:
    case SampleSubType.GRAY_WATER:
    case SampleSubType.BLACK_WATER:
    case SampleSubType.SEPTIC_TANK_RAW:
    case SampleSubType.SEPTIC_TANK_TREATED:
    case SampleSubType.STORMWATER:
    case SampleSubType.URBAN_RUNOFF:
    case SampleSubType.UNSPECIFIED_SEWAGE_WASTEWATER:
    case SampleSubType.OTHER_WASTEWATER:
      return SampleType.WASTEWATER;
    case SampleSubType.SEAWATER:
    case SampleSubType.UNSPECIFIED_SURFACE_WATER:
    case SampleSubType.UNSPECIFIED_SURFACE_WATER_UPSTREAM:
    case SampleSubType.UNSPECIFIED_SURFACE_WATER_DOWNSTREAM:
    case SampleSubType.ESTUARY_WATER_UPSTREAM:
    case SampleSubType.ESTUARY_WATER_DOWNSTREAM:
    case SampleSubType.COASTAL_WATER_UPSTREAM:
    case SampleSubType.COASTAL_WATER_DOWNSTREAM:
    case SampleSubType.FRESH_RIVER_WATER_UPSTREAM:
    case SampleSubType.FRESH_RIVER_WATER_DOWNSTREAM:
    case SampleSubType.FRESH_LAKE_WATER:
    case SampleSubType.WETLAND_WATER:
      return SampleType.SURFACE_WATER;
    case SampleSubType.INDUSTRIAL_WATER:
    case SampleSubType.DRINKING_WATER:
    case SampleSubType.RECLAIMED_WATER:
    case SampleSubType.RAINWATER:
    case SampleSubType.GROUNDWATER:
    case SampleSubType.SPRING_WATER:
    case SampleSubType.SHRIMP_CULTURE_WATER:
    case SampleSubType.FISH_CULTURE_WATER:
    case SampleSubType.CHEMICALLY_DISINFECTED_WATER:
      return SampleType.OTHER_WATER;
    case SampleSubType.SEDIMENT_RIVER:
    case SampleSubType.SEDIMENT_LAKE:
    case SampleSubType.SEDIMENT_BRACKISH:
    case SampleSubType.SEDIMENT_SEABED:
    case SampleSubType.SHRIMP_CULTURE_SEDIMENT:
    case SampleSubType.FISH_CULTURE_SEDIMENT:
      return SampleType.SEDIMENT;
    case SampleSubType.AGRICULTURAL_SOIL:
    case SampleSubType.URBAN_SOIL:
    case SampleSubType.FOREST_SOIL:
    case SampleSubType.SAND:
    case SampleSubType.OTHER_NATURAL_SOIL:
    case SampleSubType.OTHER_SOIL:
      return SampleType.SOIL;
    case SampleSubType.HUMAN_STOOL:
    case SampleSubType.WILD_ANIMAL_STOOL:
    case SampleSubType.CAT_STOOL:
    case SampleSubType.DOG_STOOL:
    case SampleSubType.OTHER_COMPANION_ANIMAL_STOOL:
    case SampleSubType.PRODUCTION_CATTLE_STOOL:
    case SampleSubType.PRODUCTION_SWINE_STOOL:
    case SampleSubType.PRODUCTION_POULTRY_STOOL:
    case SampleSubType.PRODUCTION_SHEEP_STOOL:
    case SampleSubType.PRODUCTION_ANIMAL_OTHER_STOOL:
      return SampleType.STOOL;
    case SampleSubType.CATTLE_MANURE:
    case SampleSubType.SWINE_MANURE:
    case SampleSubType.SHEEP_MANURE:
    case SampleSubType.POULTRY_MANURE:
    case SampleSubType.OTHER_MANURE:
      return SampleType.MANURE;
    case SampleSubType.ACTIVATED_SLUDGE:
    case SampleSubType.SEWAGE_SLUDGE:
      return SampleType.SLUDGE;
    case SampleSubType.BEEF:
    case SampleSubType.PORK:
    case SampleSubType.POULTRY_MEAT:
    case SampleSubType.DAIRY:
    case SampleSubType.FISH:
    case SampleSubType.SHELLFISH:
    case SampleSubType.SHRIMP:
    case SampleSubType.FRUITS:
    case SampleSubType.VEGETABLES:
    case SampleSubType.LEGUMES:
    case SampleSubType.OTHER_PLANT_BASED_FOOD:
    case SampleSubType.MEAT_OTHER:
    case SampleSubType.FOOD_OTHER:
      return SampleType.FOOD;
    case SampleSubType.NTC:
    case SampleSubType.POSITIVE_CONTROL:
    case SampleSubType.OTHER_CONTROL:
      return SampleType.CONTROL;
    case SampleSubType.ISOLATE:
    case SampleSubType.BIOFILM:
    case SampleSubType.ENVIRONMENTAL_SWAB:
    case SampleSubType.ANIMAL_FEED:
    case SampleSubType.COMPOST:
    case SampleSubType.DUST:
    case SampleSubType.AIR:
    case SampleSubType.BLOOD_HUMAN:
      return SampleType.OTHER;
  }
}

/*
    NOTE WHEN UPDATING SamplingPointSubTypes, THEIR NAMES OR TYPE MAPPINGS!
    - _RAW, _TREATED, _UPSTREAM, _DOWNSTREAM suffixes are expected for pooling samples for process performance evaluation (see: SmplPntSubTypePostFix)
    - Update comparableGroupSpecs if needed
    - Update SamplingPointSubType, getParentType and friendlySamplingPointSubType
    - Update Customer template 'Sample types™ sheet
*/
export function isLegalSubtype(subType: SampleSubType | null | undefined, type: SampleType): boolean {
  if (isNil(subType)) {
    return true;
  }
  const expectedType = getParentType(subType);
  return type === expectedType;
}

export function friendlySampleSubTypeOrType(subtypeOrType: SampleSubType | SampleType): string {
  if (get(SampleSubType, subtypeOrType, undefined)) {
    const subtype = subtypeOrType as SampleSubType;
    return friendlySampleSubType(subtype, getParentType(subtype));
  } else {
    return friendlySampleType(subtypeOrType as SampleType);
  }
}

/*
    NOTE WHEN UPDATING SamplingPointSubTypes, THEIR NAMES OR TYPE MAPPINGS!
    - _RAW, _TREATED, _UPSTREAM, _DOWNSTREAM suffixes are expected for pooling samples for process performance evaluation (see: SmplPntSubTypePostFix)
    - Update comparableGroupSpecs if needed
    - Update SamplingPointSubType, getParentType and friendlySamplingPointSubType
    - Update Customer template 'Sample types™ sheet
*/
export function friendlySampleSubType(subType: SampleSubType | null | undefined, _type?: SampleType): string {
  const type = _type ?? (subType && getParentType(subType));
  if (!type) {
    throw new Error('No type or subtype provided');
  }
  switch (subType) {
    // WASTEWATER
    case SampleSubType.HOSPITAL_WASTEWATER_RAW:
      return 'Hospital wastewater (raw)'; // clinical (though has feces)
    case SampleSubType.HOSPITAL_WASTEWATER_TREATED:
      return 'Hospital wastewater (treated)';
    case SampleSubType.PRIMARY_HEALTH_CARE_RAW:
      return 'Primary health care wastewater (raw)';
    case SampleSubType.PRIMARY_HEALTH_CARE_TREATED:
      return 'Primary health care wastewater (treated)';
    case SampleSubType.URBAN_WASTEWATER_RAW:
      return 'Urban wastewater (raw)'; // public health / human feces
    case SampleSubType.URBAN_WASTEWATER_TREATED:
      return 'Urban wastewater (treated)';
    case SampleSubType.INDUSTRIAL_WASTEWATER_RAW:
      return 'Industrial wastewater (raw)';
    case SampleSubType.INDUSTRIAL_WASTEWATER_TREATED:
      return 'Industrial wastewater (treated)';
    case SampleSubType.AGRICULTURAL_WASTEWATER_RAW:
      return 'Agricultural wastewater (raw)';
    case SampleSubType.AGRICULTURAL_WASTEWATER_TREATED:
      return 'Agricultural wastewater (treated)';
    case SampleSubType.AQUACULTURE_WASTEWATER_RAW:
      return 'Aquaculture wastewater (raw)';
    case SampleSubType.AQUACULTURE_WASTEWATER_TREATED:
      return 'Aquaculture wastewater (treated)';
    case SampleSubType.GRAY_WATER:
      return 'Gray water';
    case SampleSubType.BLACK_WATER:
      return 'Black water'; // public health / human feces
    case SampleSubType.SEPTIC_TANK_RAW:
      return 'Septic tank (initial compartment)'; // public health / human feces
    case SampleSubType.SEPTIC_TANK_TREATED:
      return 'Septic tank (final compartment)';
    case SampleSubType.STORMWATER:
      return 'Stormwater'; // maybe (no)
    case SampleSubType.URBAN_RUNOFF:
      return 'Urban runoff'; // public health / human feces
    case SampleSubType.UNSPECIFIED_SEWAGE_WASTEWATER:
      return 'Unspecified sewage wastewater';
    case SampleSubType.OTHER_WASTEWATER:
      return 'Other wastewater';
    // SURFACE WATER
    case SampleSubType.ESTUARY_WATER_UPSTREAM:
      return 'Estuary water (w/o WW discharge)';
    case SampleSubType.ESTUARY_WATER_DOWNSTREAM:
      return 'Estuary water (with WW discharge)';
    case SampleSubType.SEAWATER:
      return 'Seawater';
    case SampleSubType.COASTAL_WATER_UPSTREAM:
      return 'Coastal water (w/o WW discharge)';
    case SampleSubType.COASTAL_WATER_DOWNSTREAM:
      return 'Coastal water (after WW discharge)';
    case SampleSubType.FRESH_LAKE_WATER:
      return 'Lake or pond water';
    case SampleSubType.FRESH_RIVER_WATER_UPSTREAM:
      return 'River or stream water (w/o WW discharge)';
    case SampleSubType.FRESH_RIVER_WATER_DOWNSTREAM:
      return 'River or stream water (after WW discharge)';
    case SampleSubType.UNSPECIFIED_SURFACE_WATER:
      return 'Unspecified surface water';
    case SampleSubType.UNSPECIFIED_SURFACE_WATER_UPSTREAM:
      return 'Unspecified surface water (w/o WW discharge)';
    case SampleSubType.UNSPECIFIED_SURFACE_WATER_DOWNSTREAM:
      return 'Unspecified surface water (with WW discharge)';
    case SampleSubType.WETLAND_WATER:
      return 'Wetland water';
    // OTHER WATER
    case SampleSubType.INDUSTRIAL_WATER:
      return 'Industrial water';
    case SampleSubType.DRINKING_WATER:
      return 'Drinking water';
    case SampleSubType.RECLAIMED_WATER:
      return 'Reclaimed or recycled water';
    case SampleSubType.CHEMICALLY_DISINFECTED_WATER:
      return 'Chemically disinfected water';
    case SampleSubType.RAINWATER:
      return 'Rainwater';
    case SampleSubType.GROUNDWATER:
      return 'Groundwater';
    case SampleSubType.SPRING_WATER:
      return 'Spring water';
    case SampleSubType.FISH_CULTURE_WATER:
      return 'Fish culture water';
    case SampleSubType.SHRIMP_CULTURE_WATER:
      return 'Shrimp culture water';
    // SEDIMENT
    case SampleSubType.SEDIMENT_RIVER:
      return 'River sediment';
    case SampleSubType.SEDIMENT_LAKE:
      return 'Lake sediment';
    case SampleSubType.SEDIMENT_BRACKISH:
      return 'Brackish water sediment';
    case SampleSubType.SEDIMENT_SEABED:
      return 'Seabed sediment';
    case SampleSubType.SHRIMP_CULTURE_SEDIMENT:
      return 'Shrimp culture pond sediment';
    case SampleSubType.FISH_CULTURE_SEDIMENT:
      return 'Fish culture pond sediment';
    // SOIL
    case SampleSubType.AGRICULTURAL_SOIL:
      return 'Agricultural soil';
    case SampleSubType.URBAN_SOIL:
      return 'Urban soil';
    case SampleSubType.FOREST_SOIL:
      return 'Forest soil';
    case SampleSubType.OTHER_NATURAL_SOIL:
      return 'Other natural soil';
    case SampleSubType.SAND:
      return 'Sand';
    case SampleSubType.OTHER_SOIL:
      return 'Other soil';
    // STOOL
    case SampleSubType.HUMAN_STOOL:
      return 'Human stool';
    case SampleSubType.WILD_ANIMAL_STOOL:
      return 'Wild animal stool';
    case SampleSubType.CAT_STOOL:
      return 'Cat stool';
    case SampleSubType.DOG_STOOL:
      return 'Dog stool';
    case SampleSubType.OTHER_COMPANION_ANIMAL_STOOL:
      return 'Other companion animal stool';
    case SampleSubType.PRODUCTION_CATTLE_STOOL:
      return 'Production cattle stool';
    case SampleSubType.PRODUCTION_SWINE_STOOL:
      return 'Production swine stool';
    case SampleSubType.PRODUCTION_POULTRY_STOOL:
      return 'Production poultry stool';
    case SampleSubType.PRODUCTION_SHEEP_STOOL:
      return 'Production sheep stool';
    case SampleSubType.PRODUCTION_ANIMAL_OTHER_STOOL:
      return 'Production animal stool (other)';
    // MANURE
    case SampleSubType.CATTLE_MANURE:
      return 'Cattle manure';
    case SampleSubType.SWINE_MANURE:
      return 'Swine manure';
    case SampleSubType.SHEEP_MANURE:
      return 'Sheep manure';
    case SampleSubType.POULTRY_MANURE:
      return 'Poultry manure';
    case SampleSubType.OTHER_MANURE:
      return 'Other manure';
    // SLUDGE
    case SampleSubType.ACTIVATED_SLUDGE:
      return 'Activated sludge';
    case SampleSubType.SEWAGE_SLUDGE:
      return 'Sewage sludge';
    // Food
    case SampleSubType.BEEF:
      return 'Beef';
    case SampleSubType.PORK:
      return 'Pork';
    case SampleSubType.POULTRY_MEAT:
      return 'Poultry meat';
    case SampleSubType.DAIRY:
      return 'Dairy';
    case SampleSubType.FISH:
      return 'Fish';
    case SampleSubType.SHELLFISH:
      return 'Shellfish';
    case SampleSubType.SHRIMP:
      return 'Shrimp';
    case SampleSubType.FRUITS:
      return 'Fruits';
    case SampleSubType.VEGETABLES:
      return 'Vegetables';
    case SampleSubType.LEGUMES:
      return 'Legumes';
    case SampleSubType.OTHER_PLANT_BASED_FOOD:
      return 'Plant based (other)';
    case SampleSubType.MEAT_OTHER:
      return 'Meat (other)';
    case SampleSubType.FOOD_OTHER:
      return 'Food (other)';
    // Control
    case SampleSubType.NTC:
      return 'No template control';
    case SampleSubType.POSITIVE_CONTROL:
      return 'Positive control';
    case SampleSubType.OTHER_CONTROL:
      return 'Other control';
    // Other
    case SampleSubType.ISOLATE:
      return 'Isolate';
    case SampleSubType.BIOFILM:
      return 'Biofilm';
    case SampleSubType.ENVIRONMENTAL_SWAB:
      return 'Environmental swab';
    case SampleSubType.COMPOST:
      return 'Compost';
    case SampleSubType.ANIMAL_FEED:
      return 'Animal feed';
    case SampleSubType.DUST:
      return 'Dust';
    case SampleSubType.AIR:
      return 'Air';
    case SampleSubType.BLOOD_HUMAN:
      return 'Blood (human)';
    case undefined:
    case null:
      return friendlySampleType(type);
  }
}

export function friendlySampleType(type: SampleType): string {
  switch (type) {
    case SampleType.WASTEWATER:
      return 'Wastewater';
    case SampleType.SURFACE_WATER:
      return 'Surface water';
    case SampleType.OTHER_WATER:
      return 'Water (other)';
    case SampleType.SEDIMENT:
      return 'Sediment';
    case SampleType.SLUDGE:
      return 'Sludge';
    case SampleType.SOIL:
      return 'Soil';
    case SampleType.STOOL:
      return 'Stool';
    case SampleType.MANURE:
      return 'Manure';
    case SampleType.FOOD:
      return 'Food';
    case SampleType.CONTROL:
      return 'Lab control';
    case SampleType.OTHER:
      return 'Other';
  }
}
